<template>
  <div class="flex flex-col">

    <div class="header">{{ header }}<span class="header-add-on">{{ headerAddOn }}</span></div>

    <div class="flex items-center justify-between mb-2" v-if="speechRecognitionOn">
      <div class="stop-hint" v-html="stopHintTxt()"></div>
      <div class="language-selector">
        <select v-if="languages" v-model="activeLanguage">
          <option v-for="(lang, index) in languages" :key="index" :value="lang.code" :selected="lang.code === activeLanguage">{{ lang.label }}</option>
        </select>
      </div>
    </div>

    <div class="form-field-wrapper" :class="{ 'voice-recognition-on': speechRecognitionOn }">
      <svg class="icon icon-mic">
        <use xlink:href="#micro-off" class="text-gray-500" @click="startSpeechRecordTitle()" v-if="!speechRecognitionOn" />
        <use xlink:href="#micro-on" class="text-green-600" @click="stopSpeechRecordTitle()" v-if="speechRecognitionOn" />
      </svg>

      <input ref="input-field" type="text" :value="inputFieldText" @input="updateTitle()">

      <svg class="icon icon-cross" @click="clearInputFieldText()">
        <use xlink:href="#cross" />
      </svg>

    </div>

    <div class="remark">{{ remark }}</div>
  </div>
</template>

<script>
// // Move to main-js and make them prototypes
// Vue.prototype.$speechRecognition = window...
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition || null
// const SpeechGrammarList = window.SpeechGrammarList || window.webkitSpeechGrammarList || null
// const SpeechRecognitionEvent = window.SpeechRecognitionEvent || window.webkitSpeechRecognitionEvent || null

export default {
  name: 'Input',
  props: {
    header: {
      type: String,
      required: true
    },
    headerAddOn: {
      type: String,
      required: false
    },
    stopHint: {
      type: String,
      required: true
    },
    remark: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      // This var will be synchronised with whatever var you use for the v-model
      // on the parent component (e.g. "newActivityTitle")
      inputFieldText: '',
      //
      // Speech Regognition: should come from store and GeoMoverDB!!
      //
      speechRecognitionAvailable: false,
      speechRecognitionOn: false,
      speechRecognizer: null,
      // override debugging (coming from mixin) locally
      localDebugging: false
    }
  },
  computed: {
    stopCommand: {
      get () { return this.$store.getters['speechRecognition/getActiveStopCommand'] },
      set (val) { this.$store.dispatch('speechRecognition/setActiveStopCommand', val) }
    },
    activeLanguage: {
      get () { return this.$store.getters['speechRecognition/getActiveLanguage'] },
      set (val) {
        this.$store.dispatch('speechRecognition/setActiveLanguage', val)

        if (this.speechRecognitionOn) {
          this.speechRecognizer.lang = val
        }
      }
    },
    languages: {
      get () { return this.$store.getters['speechRecognition/getLanguages'] }
    }
  },
  methods: {
    updateTitle () {
      // this.log = 'Input Component: updateTitle - invoked<br>'
      // this.log = '> this.$refs[input-field] = ' + this.$refs['input-field'] + '<br>'
      // this.log = '> this.$refs[input-field].value = ' + this.$refs['input-field'].value + '<br>'

      // @src: https://vuejs.org/v2/guide/components-props.html#Disabling-Attribute-Inheritance
      // v-on:input="$emit('input', $event.target.value)"
      this.$emit('input', {
        string: this.$refs['input-field'].value
      })
    },
    stopHintTxt () {
      return `${this.stopHint}: <b><i>${this.stopCommand}</i></b>`
    },
    checkIfSpeechRecognitionIsAvailable () {
      // this.log = 'Input component: checkIfSpeechRecognitionIsAvailable - invoked'
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        this.log = '> speechRecognition available'
        this.speechRecognitionAvailable = true
      }
      else {
        this.log = '> speechRecognition not available'
        this.speechRecognitionAvailable = false
      }
    },
    startSpeechRecordTitle () {
      // this.log = 'Input component: startSpeechRecordTitle - invoked'
      this.speechRecognitionOn = true

      // const titleField = this.$refs['input-field']
      this.speechRecognizer = new SpeechRecognition()

      // TODO: make "continuous" and "interimResults" flexible... through a setting? (textarea needs continuous, input not)

      // Recognizer doesn't stop listening even if the user pauses
      this.speechRecognizer.continuous = false

      // this is not reactive, so set it again in computed properties, when a new language is chosen
      this.speechRecognizer.lang = this.activeLanguage

      // Probably good for textarea
      // this.speechRecognizer.interimResults = true

      // this.log = 'Input component: this.speechRecognizer = ' + this.speechRecognizer + '<br>'

      // Start recognising
      this.speechRecognizer.onresult = event => {
        this.log = 'Input component: event = ' + event + '<br>'

        for (let i = event.resultIndex; i < event.results.length; i++) {
          this.log = 'Input component: startSpeechRecordTitle - i = ' + i + '<br>'
          if (event.results[i].isFinal) {
            this.log = 'Input component: startSpeechRecordTitle - is final: ' + event.results[i][0].transcript.trim() + '<br>'

            if (event.results[i][0].transcript.toLowerCase().trim() === 'stop' || event.results[i][0].transcript.toLowerCase().trim() === 'stopp') {
              this.log = 'Input component: startSpeechRecordTitle - Heard stop: ' + event.results[i][0].transcript.trim() + ' - stopping voice recognition<br>'
              this.stopSpeechRecordTitle()
            }
            else {
              let txt = event.results[i][0].transcript.trim()
              txt = txt.charAt(0).toUpperCase() + txt.slice(1)
              this.inputFieldText = txt
              this.$refs['input-field'].value = txt
              this.updateTitle()
              this.stopSpeechRecordTitle()
            }
          }
          else {
            this.log = 'Input component: startSpeechRecordTitle - is NOT final<br>'
            let txt = event.results[i][0].transcript.trim()
            if (i === 0) {
              txt = txt.charAt(0).toUpperCase + txt.slice(1)
            }
            this.inputFieldText += txt
            this.$refs['input-field'].value += txt
            this.updateTitle()
          }
        }
      }

      // Listen for errors
      this.speechRecognizer.onerror = event => {
        this.log = 'Input component: SpeechRecognition error: ' + event.message + '<br>'
      }

      try {
        this.speechRecognizer.start()
      }
      catch (error) {
        this.log = 'Input component: SpeechRecognition error: ' + error.message + '<br>'
      }
    },
    stopSpeechRecordTitle () {
      this.log = 'Input component: stopSpeechRecordTitle - invoked'
      this.speechRecognitionOn = false
      this.speechRecognizer.stop()
    },
    clearInputFieldText () {
      this.inputFieldText = ''
    }
  },
  mounted () {
    this.checkIfSpeechRecognitionIsAvailable()
    this.updateTitle()
  }
}
</script>

<style lang="postcss" scoped>
.header {
  @apply select-none pointer-events-none mb-2
}
.header-add-on {
  @apply text-xs font-semibold uppercase text-gray-400 ml-2 select-none pointer-events-none
}
.language-selector {
  @apply text-sm
}
select {
  @apply bg-pink-800 text-pink-200 rounded px-2 py-1 w-16
}
.form-field-wrapper {
  @apply flex items-center rounded border border-pink-800 bg-white mb-2
}
.form-field-wrapper.voice-recognition-on,
.form-field-wrapper:focus-within {
  @apply ring-pink-800 ring-2 ring-offset-2
}
.icon {
  @apply w-10 h-10 p-0 cursor-pointer select-none flex-shrink-0 bg-white transition-colors
}
.icon:hover {
  @apply bg-pink-100
}
.icon-mic {
  @apply rounded-l
}
.icon-cross {
  @apply px-2 rounded-r
}
input {
  @apply px-2 w-full p-0 m-0
}
input:focus {
  @apply ring-0 ring-opacity-0 ring-offset-0
}
.stop-hint {
  @apply text-green-700 mb-2 text-sm
}
.remark {
  @apply mb-3 text-xs text-gray-400 leading-none select-none pointer-events-none
}
</style>
